import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import styled from '@emotion/styled';

import { FontWeight } from '@clutter/clean';

import { useTrack } from '@root/initializers/wt';

import { CustomLink } from '@shared/custom_link';

import breadcrumbChevron from '@images/checkout/challenger/breadcrumb_chevron.svg';
import breadcrumbChevronMobile from '@images/checkout/challenger/breadcrumb_chevron_mobile.svg';
import phoneIcon from '@images/checkout/challenger/phone.svg';
import phoneMobile from '@images/checkout/challenger/phone_mobile.svg';
import logoEndorserBlack from '@images/home/logo-clutter-endorser-black.png';

import { TABLET_UP as DESKTOP, MOBILE } from './media';
import { SaveProgressModal } from './save_progress_modal';
import { TalkWithExpertModal } from './talk_with_expert_modal';
import { CHALLENGER as T } from './tokens';

export type ChallengerNavStep = 'storage_plan' | 'confirm_reservation';

type Props = {
  step: ChallengerNavStep;
  /** Password page uses a simpler nav (logo + Need help?). */
  variant?: 'checkout' | 'password';
};

const Bar = styled.header<{ $elevated?: boolean }>`
  position: sticky;
  top: 0;
  /* Above portaled talk modal (z-index 1000) so phone/Close stay usable. */
  z-index: ${({ $elevated }) => ($elevated ? 1001 : 20)};
  background: ${T.color.bgPage};
  border-bottom: 1px solid ${T.color.borderDefault};
  font-family: ${T.font.sans};
`;

const Inner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1424px;
  margin: 0 auto;
  box-sizing: border-box;

  ${MOBILE} {
    gap: 12px;
    padding: ${T.space[8]} ${T.space[8]} ${T.space[8]} 0;
  }

  ${DESKTOP} {
    gap: ${T.space[48]};
    padding: ${T.space[12]} ${T.space[32]};
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: ${T.space[8]};
  flex: 1 1 auto;
  min-width: 0;

  ${MOBILE} {
    overflow: hidden;
  }
`;

const LogoLink = styled(CustomLink)<{ $always?: boolean }>`
  display: ${({ $always }) => ($always ? 'inline-flex' : 'none')};
  flex-shrink: 0;
  padding: 3px 4px 4px;

  ${MOBILE} {
    ${({ $always }) =>
      $always
        ? `
      padding: 2.83px;
      align-items: flex-start;
    `
        : ''}
  }

  ${DESKTOP} {
    display: inline-flex;
  }
`;

/** Figma 2291:30522 — homepage endorser logo (Clutter by Iron Mountain). */
const Logo = styled.img`
  display: block;
  height: 29px;
  width: auto;
  max-width: 91px;
  object-fit: contain;
  object-position: left center;

  ${MOBILE} {
    height: 24px;
    max-width: 76px;
  }
`;

const Crumb = styled.nav`
  display: flex;
  align-items: center;
  min-width: 0;

  ${MOBILE} {
    gap: 0;
    flex: 1 1 auto;
    overflow: hidden;

    /* Figma 707:20853 — first crumb + chevron use mr-[-14px] to tighten. */
    & > :first-of-type {
      margin-right: -14px;
    }
  }

  ${DESKTOP} {
    gap: ${T.space[8]};
    margin-left: ${T.space[16]};
  }
`;

/**
 * Figma mobile nav (707:20307): Demi Bold 14/12, text-secondary active,
 * text-subtle inactive. Desktop (707:10044): 16/16 primary + 40% muted.
 */
const CrumbCurrent = styled.span`
  font-family: ${T.font.sans};
  font-weight: ${FontWeight.Semibold};
  font-size: 16px;
  line-height: 16px;
  color: ${T.color.textPrimary};
  white-space: nowrap;
  flex-shrink: 0;

  ${MOBILE} {
    font-size: 14px;
    line-height: 14px;
    padding: 0 ${T.space[16]};
    color: ${T.color.textSecondary};
  }

  ${DESKTOP} {
    padding: 0 4px;
  }
`;

const CrumbMuted = styled.span`
  font-family: ${T.font.sans};
  font-weight: ${FontWeight.Semibold};
  font-size: 16px;
  line-height: 16px;
  color: ${T.color.textSubtle};
  white-space: nowrap;
  min-width: 0;

  ${MOBILE} {
    font-size: 14px;
    line-height: 14px;
    padding: 0 ${T.space[16]};
    overflow: hidden;
    text-overflow: ellipsis;
  }

  ${DESKTOP} {
    padding: 0 4px;
    color: ${T.color.textPrimary};
    opacity: 0.4;
  }
`;

/**
 * Figma icon/keyboard_arrow_up (707:10044 desktop), rotated 90° → “>”.
 */
const CrumbChevron = styled.img`
  display: block;
  flex-shrink: 0;
  object-fit: contain;
  transform: rotate(90deg);

  ${MOBILE} {
    display: none;
  }

  ${DESKTOP} {
    width: 16px;
    height: 16px;
  }
`;

/**
 * Figma 707:20307 — 24×24 icon/keyboard_arrow_up hit box; glyph is 12×7.41
 * inset (not scaled to fill 24px). Rotate 90° for “>”. Stretching the img to
 * 24×24 then rotating overflow-clips into a chunky/weird chevron.
 */
const CrumbChevronMobile = styled.span`
  display: none;
  flex-shrink: 0;

  ${MOBILE} {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin-right: -14px;
    overflow: hidden;
  }
`;

const CrumbChevronMobileGlyph = styled.img`
  display: block;
  width: 12px;
  height: 7.41px;
  transform: rotate(90deg);
`;
/** Figma 707:20307 shortens the second crumb to “Confirm” on mobile. */
const CrumbDesktopOnly = styled.span`
  ${MOBILE} {
    display: none;
  }
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  position: relative;
  z-index: 1;

  ${MOBILE} {
    gap: 12px;
  }

  ${DESKTOP} {
    gap: ${T.space[8]};
  }
`;

/**
 * Figma 707:10044 default = transparent; 707:14687 open/pressed =
 * button-bg-secondary-hover (#c8d1d3).
 */
const TalkButton = styled.button<{ $active?: boolean }>`
  appearance: none;
  display: none;
  align-items: center;
  justify-content: center;
  gap: ${T.space[8]};
  height: 48px;
  padding: 0 ${T.space[24]} 0 ${T.space[16]};
  border: none;
  border-radius: ${T.radius.sm};
  background: ${({ $active }) =>
    $active ? T.color.bgButtonSecondaryHover : 'transparent'};
  cursor: pointer;
  font-family: ${T.font.sans};
  /* Figma Button/Large — Demi Bold 16/16 text-primary */
  font-size: 16px;
  font-weight: ${FontWeight.Semibold};
  line-height: 16px;
  color: ${T.color.textPrimary};

  ${DESKTOP} {
    display: inline-flex;
  }

  &:hover {
    background: ${T.color.bgButtonSecondaryHover};
  }
`;

const PhoneImg = styled.img`
  width: 20px;
  height: 20px;
  display: block;
`;

/** Anchor for Talk-with-expert popover (sizes to the visible Talk control). */
const TalkAnchor = styled.div`
  display: inline-flex;
  align-items: center;
`;

/**
 * Figma 707:23349 — open/pressed uses border-default fill
 * (rgba(154,161,165,0.3)); default matches Close secondary fill (0.2).
 */
const PhoneIconButton = styled.button<{ $active?: boolean }>`
  appearance: none;
  display: none;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 36px;
  padding: 4px ${T.space[16]};
  border: none;
  border-radius: ${T.radius.sm};
  background: ${({ $active }) =>
    $active ? 'rgba(154, 161, 165, 0.3)' : 'rgba(154, 161, 165, 0.2)'};
  cursor: pointer;
  box-sizing: border-box;

  ${MOBILE} {
    display: inline-flex;
  }
`;

const PhoneMobileImg = styled.img`
  width: 20px;
  height: 20px;
  display: block;
`;

const SaveButton = styled.button`
  appearance: none;
  display: none;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 ${T.space[24]};
  border-radius: ${T.radius.sm};
  border: 1px solid ${T.color.borderDefault};
  background: ${T.color.bgPrimary};
  cursor: pointer;
  font-family: ${T.font.sans};
  font-size: 16px;
  font-weight: ${FontWeight.Semibold};
  line-height: 16px;
  color: ${T.color.textPrimary};
  white-space: nowrap;
  box-sizing: border-box;

  ${DESKTOP} {
    display: inline-flex;
  }

  &:hover {
    opacity: 0.9;
  }
`;

const CloseLink = styled(CustomLink)`
  display: none;
  align-items: center;
  justify-content: center;
  padding: ${T.space[12]} ${T.space[16]};
  border-radius: ${T.radius.sm};
  background: rgba(154, 161, 165, 0.2);
  font-family: ${T.font.sans};
  font-size: 12px;
  font-weight: ${FontWeight.Semibold};
  line-height: 14px;
  color: ${T.color.textPrimary};
  text-decoration: none;
  white-space: nowrap;
  box-sizing: border-box;

  ${MOBILE} {
    display: inline-flex;
  }
`;

const HelpButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: ${T.radius.sm};
  background: rgba(154, 161, 165, 0.2);
  cursor: pointer;
  font-family: ${T.font.sans};
  font-weight: ${FontWeight.Semibold};
  color: ${T.color.textPrimary};

  ${MOBILE} {
    padding: ${T.space[12]} ${T.space[16]};
    font-size: 12px;
    line-height: 14px;
  }

  ${DESKTOP} {
    height: 48px;
    padding: 0 ${T.space[24]};
    font-size: 16px;
    line-height: 16px;
    background: ${T.color.bgButtonDisabled};
  }

  &:hover {
    opacity: 0.9;
  }
`;

export const ChallengerNav: React.FC<Props> = ({
  step,
  variant = 'checkout',
}) => {
  const history = useHistory();
  const location = useLocation();
  const track = useTrack({ action: 'click' });
  const [saveOpen, setSaveOpen] = useState(false);
  const [talkOpen, setTalkOpen] = useState(false);
  const talkAnchorRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);

  /** Deep link `?schedule_call=true` opens the same Figma modal. */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('schedule_call') !== 'true') return;
    setTalkOpen(true);
    params.delete('schedule_call');
    const search = params.toString();
    history.replace({ search: search ? `?${search}` : '' });
  }, [history, location.search]);

  const onTalk = () => {
    track({
      objectName: 'talk_with_expert',
      objectType: 'button',
      label: 'Talk with an expert',
    });
    setTalkOpen((open) => !open);
  };

  const isPassword = variant === 'password';

  return (
    <Bar ref={navRef} $elevated={talkOpen}>
      <Inner>
        <Left>
          <LogoLink to="/" aria-label="Clutter home" $always={isPassword}>
            <Logo src={logoEndorserBlack} alt="Clutter by Iron Mountain" />
          </LogoLink>
          {!isPassword && (
            <Crumb aria-label="Checkout progress">
              {step === 'storage_plan' ? (
                <CrumbCurrent aria-current="page">Storage plan</CrumbCurrent>
              ) : (
                <CrumbMuted>Storage plan</CrumbMuted>
              )}
              <CrumbChevron src={breadcrumbChevron} alt="" aria-hidden />
              <CrumbChevronMobile aria-hidden>
                <CrumbChevronMobileGlyph src={breadcrumbChevronMobile} alt="" />
              </CrumbChevronMobile>
              {step === 'confirm_reservation' ? (
                <CrumbCurrent aria-current="page">
                  Confirm
                  <CrumbDesktopOnly> reservation</CrumbDesktopOnly>
                </CrumbCurrent>
              ) : (
                <CrumbMuted>
                  Confirm
                  <CrumbDesktopOnly> reservation</CrumbDesktopOnly>
                </CrumbMuted>
              )}
            </Crumb>
          )}
        </Left>
        <Right>
          {isPassword ? (
            <div ref={talkAnchorRef}>
              <HelpButton
                type="button"
                aria-expanded={talkOpen}
                aria-haspopup="dialog"
                onClick={onTalk}
              >
                Need help?
              </HelpButton>
            </div>
          ) : (
            <>
              <TalkAnchor ref={talkAnchorRef}>
                <TalkButton
                  type="button"
                  $active={talkOpen}
                  aria-expanded={talkOpen}
                  aria-haspopup="dialog"
                  onClick={onTalk}
                >
                  <PhoneImg src={phoneIcon} alt="" />
                  Talk with an expert
                </TalkButton>
                <PhoneIconButton
                  type="button"
                  $active={talkOpen}
                  aria-label="Talk with an expert"
                  aria-expanded={talkOpen}
                  aria-haspopup="dialog"
                  onClick={onTalk}
                >
                  <PhoneMobileImg src={phoneMobile} alt="" />
                </PhoneIconButton>
              </TalkAnchor>
              <SaveButton
                type="button"
                onClick={() => {
                  track({
                    objectName: 'save_progress',
                    objectType: 'button',
                    label: 'Save',
                  });
                  setSaveOpen(true);
                }}
              >
                Save
              </SaveButton>
              <CloseLink to="/">Close</CloseLink>
              <SaveProgressModal
                isOpen={saveOpen}
                onClose={() => setSaveOpen(false)}
              />
            </>
          )}
          <TalkWithExpertModal
            isOpen={talkOpen}
            onClose={() => setTalkOpen(false)}
            anchorRef={talkAnchorRef}
            navRef={navRef}
          />
        </Right>
      </Inner>
    </Bar>
  );
};
